import { reactive } from 'vue';
import Cookies from 'js-cookie';
import {
  AUTH_COOKIE_NAME,
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from 'dashboard/store/utils/api';
import { listenAuthSignals } from 'dashboard/helper/authSignal';

// One browser profile shares a single auth cookie (cw_d_session_info) between all its
// dashboard tabs, but each tab sends the headers it holds itself. This keeps the two
// apart:
// - a tab writes the cookie only when its own headers changed (a rotated token) and
//   the cookie is empty or belongs to the same account, never over another account;
// - a sign-out in one tab sends the other tabs of that account to the login page;
// - a tab whose cookie now belongs to another account shows a banner to refresh.
export const ACCOUNT_CHECK_INTERVAL_MS = 15000;

const AUTH_KEYS = ['access-token', 'token-type', 'client', 'expiry', 'uid'];

export const authSyncState = reactive({
  ownUid: null,
  otherUid: null,
  accountChanged: false,
});

let held = null;
let signedOutUid = null;

export const readAuthCookie = () => {
  try {
    const raw = Cookies.get(AUTH_COOKIE_NAME);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// The headers this tab sends (set at boot from the cookie, then by every change).
export const setHeldAuth = headers => {
  held = headers?.uid
    ? Object.fromEntries(AUTH_KEYS.map(key => [key, headers[key]]))
    : null;
  authSyncState.ownUid = held?.uid || null;
};

export const getHeldAuth = () => (held ? { ...held } : null);

const sameHeaders = (a, b) =>
  AUTH_KEYS.every(key => String(a?.[key] ?? '') === String(b?.[key] ?? ''));

export const headersChanged = headers => !sameHeaders(headers, held);

// (a) the headers really changed and (b) the cookie is empty or this account's.
export const mayWriteAuthCookie = headers => {
  if (!headers?.uid || !headersChanged(headers)) return false;
  // This account signed out in another tab; a late response must not bring it back.
  if (signedOutUid && signedOutUid === headers.uid) return false;
  const cookieUid = readAuthCookie()?.uid;
  return !cookieUid || cookieUid === headers.uid;
};

// Auth headers for a request made outside axios (ActiveStorage direct uploads).
export const applyAuthHeaders = xhr => {
  const headers = getHeldAuth() || readAuthCookie() || {};
  AUTH_KEYS.forEach(key => {
    if (headers[key]) xhr.setRequestHeader(key, headers[key]);
  });
};

export const checkAccount = () => {
  const own = authSyncState.ownUid;
  if (!own) return;
  const cookieUid = readAuthCookie()?.uid;
  if (cookieUid && cookieUid !== own) {
    authSyncState.accountChanged = true;
    authSyncState.otherUid = cookieUid;
  } else if (cookieUid === own) {
    authSyncState.accountChanged = false;
    authSyncState.otherUid = null;
  }
};

// Another tab signed this account out: drop the local state and go to the login page,
// without calling sign_out again.
const leaveAfterSignOut = () => {
  const ownUid = authSyncState.ownUid;
  Promise.resolve(deleteIndexedDBOnLogout())
    .catch(() => {})
    .finally(() => clearCookiesOnLogout('/app/login', { ownUid }));
};

export const handleAuthSignal = signal => {
  if (!signal?.uid || !authSyncState.ownUid) return;
  if (signal.type === 'logout') {
    if (signal.uid !== authSyncState.ownUid) return;
    signedOutUid = signal.uid;
    leaveAfterSignOut();
  } else if (signal.type === 'login') {
    checkAccount();
  }
};

let stopListening = null;
let checkTimer = null;
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') checkAccount();
};

export const stopAuthSync = () => {
  stopListening?.();
  stopListening = null;
  clearInterval(checkTimer);
  checkTimer = null;
  window.removeEventListener('focus', checkAccount);
  document.removeEventListener('visibilitychange', onVisibilityChange);
};

export const startAuthSync = () => {
  if (stopListening) return stopAuthSync;
  stopListening = listenAuthSignals(handleAuthSignal);
  window.addEventListener('focus', checkAccount);
  document.addEventListener('visibilitychange', onVisibilityChange);
  checkTimer = setInterval(checkAccount, ACCOUNT_CHECK_INTERVAL_MS);
  checkAccount();
  return stopAuthSync;
};

// Tests only.
export const resetAuthSync = () => {
  stopAuthSync();
  held = null;
  signedOutUid = null;
  authSyncState.ownUid = null;
  authSyncState.otherUid = null;
  authSyncState.accountChanged = false;
};
